import React, { useState, useEffect, useCallback } from 'react';
import { MemberList } from '../components/members/MemberList';
import { MemberForm } from '../components/members/MemberForm';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { FaUsers, FaLock } from 'react-icons/fa';
import { Modal } from '../components/common/Modal';
import { memberService } from '../services/member.service'; // 경로 수정
import type { RegisteredMember } from '../types/member';
import { usePermissions } from '../hooks/usePermissions';

export const MembersPage: React.FC = () => {
  const { canWrite, canDelete } = usePermissions();
  const [members, setMembers] = useState<RegisteredMember[]>([]);
  const [editingMember, setEditingMember] = useState<RegisteredMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const canEditMembers = canWrite('members');
  const canDeleteMembers = canDelete('members');

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      const memberData = await memberService.getAllRegisteredMembers();
      setMembers(memberData);
    } catch (err) {
      setError('등록교인 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);
  
  const handleFormSuccess = () => {
    fetchMembers();
    setIsModalOpen(false);
  };

  const handleDelete = async (id: string) => {
    if (!canDeleteMembers) {
      alert("삭제 권한이 없습니다.");
      return;
    }
    
    if (window.confirm("정말로 이 성도의 정보를 삭제하시겠습니까?")) {
      try {
        await memberService.deleteRegisteredMember(id);
        alert("삭제되었습니다.");
        fetchMembers();
      } catch (err) {
        alert("삭제 중 오류가 발생했습니다.");
      }
    }
  };

  const handleEdit = (member: RegisteredMember) => {
    if (!canEditMembers) {
      alert("수정 권한이 없습니다.");
      return;
    }
    
    setEditingMember(member);
    setIsModalOpen(true);
  };
  
  const handleAddNew = () => {
    if (!canEditMembers) {
      alert("추가 권한이 없습니다.");
      return;
    }
    
    setEditingMember(null);
    setIsModalOpen(true);
  };

  const renderContent = () => {
    if (loading) return <div className="py-10"><LoadingSpinner /></div>;
    if (error) return <p className="text-center text-red-500 bg-red-100 p-4 rounded-md">{error}</p>;
    return <MemberList members={members} onEdit={canEditMembers ? handleEdit : undefined} onDelete={canDeleteMembers ? handleDelete : undefined} />;
  };

  return (
    <div className="p-8">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
        <div className="flex items-center">
          <FaUsers className="text-3xl text-gray-600 dark:text-gray-200 mr-4" />
          <div>
            <h1 className="text-3xl font-bold text-gray-600 dark:text-gray-200">등록성도관리</h1>
            <p className="ml-1 mt-1 text-md text-gray-500 dark:text-gray-400">등록된 소중한 교인들의 정보를 관리합니다.</p>
          </div>
        </div>
        {canEditMembers ? (
          <button onClick={handleAddNew} className="px-4 py-2 bg-btn-green-bg hover:bg-btn-green-hover text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition duration-150 ease-in-out flex items-center justify-center whitespace-nowrap">
            + 등록교인추가
          </button>
        ) : (
          <div className="flex items-center px-4 py-2 bg-gray-300 text-gray-500 rounded-lg">
            <FaLock className="mr-2 h-4 w-4" />
            읽기 전용
          </div>
        )}
      </div>
      <div className="mt-8">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg">
          {renderContent()}
        </div>
      </div>
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingMember ? "성도 정보 수정" : "신규 등록교인"}>
        <MemberForm onSuccess={handleFormSuccess} editingMember={editingMember} readOnly={!canEditMembers} />
      </Modal>
    </div>
  );
};
